import { LitElement, css, html, nothing } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import "@banno/jha-wc/src/buttons/jha-button/jha-button";
import "@banno/jha-wc/src/icons/jha-icon-circle-warning";
import "@banno/jha-wc/src/icons/jha-icon-circle-checkmark";
import "@banno/jha-wc/src/icons/jha-icon-close";

/**
 * Visual / button layout for {@link JhftJhaDialog}.
 *
 * - warning — Image-2 unsaved style (orange icon, Stay + danger Leave)
 * - danger  — destructive confirm (red icon, Cancel + danger Confirm)
 * - info    — neutral confirm (info icon, Cancel + primary Confirm)
 * - success — green check, single primary button
 * - error   — red warning, Retry + Close
 * - notice  — orange warning, single primary button (forced logout / lockout)
 */
export type JhftDialogVariant = "warning" | "danger" | "info" | "success" | "error" | "notice";

/** Result returned by {@link openJhftDialog} / `jhft-dialog-result` event. */
export type JhftDialogResult = "confirm" | "cancel" | "retry";

export interface JhftDialogOptions {
  variant?: JhftDialogVariant;
  title?: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Success single-button label. */
  successLabel?: string;
  retryLabel?: string;
  closeLabel?: string;
  /**
   * When true (default), backdrop clicks do nothing — Windows-like modal.
   * When false, backdrop click closes and returns `cancel` (like Stay).
   * Either way the page behind stays blocked until the dialog closes.
   */
  modal?: boolean;
  /** ESC key (default true). Same result as the X button (`_dismissResult`). */
  closeOnEsc?: boolean;
  showCloseButton?: boolean;
}

/**
 * JHFT confirmation dialog (ACME-1050).
 *
 * Looks like the Workflow Template "Unsaved Changes" dialog (warning icon + Stay /
 * Leave Without Saving). Uses a native `<dialog showModal()>` so the page is blocked
 * while open. Unlike stock `jha-dialog` / DialogUtil, modal backdrop clicks do **not**
 * dismiss the dialog by default.
 *
 * Prefer {@link openJhftDialog} for a Promise result from any call site.
 *
 * **Dirty / unsaved rule (call-site responsibility):** do not open this dialog for
 * leave/cancel when the form has no real changes. No change → navigate/close silently.
 * See `src/utils/workflow-cancel-dirty.ts` for the Workflow Create/Edit/Clone pattern.
 * Delete / destructive confirms always prompt (not an “unsaved” case).
 *
 * @example
 * ```ts
 * const result = await openJhftDialog({
 *   variant: "warning",
 *   title: "Unsaved Changes",
 *   message: "You have unsaved changes. Are you sure you want to leave without saving?",
 *   cancelLabel: "Stay",
 *   confirmLabel: "Leave Without Saving",
 * });
 * if (result === "confirm") { /* leave *\/ }
 * ```
 */
@customElement("jhft-jha-dialog")
export class JhftJhaDialog extends LitElement {
  static styles = css`
    :host {
      display: contents;
    }

    /* Layout only while open. Unconditional display:grid overrides the UA
       dialog:not([open]) { display: none } and leaves a full-viewport hit target
       after close() / before showModal(). Popover fallback has no [open]. */
    dialog {
      border: none;
      padding: 0;
      margin: 0;
      inset: 0;
      width: 100vw;
      height: 100vh;
      max-width: 100vw;
      max-height: 100vh;
      background: transparent;
    }

    dialog[open],
    dialog:popover-open {
      display: grid;
      place-items: center;
    }

    dialog:not([open]):not(:popover-open) {
      display: none;
    }

    dialog::backdrop {
      background: rgba(0, 0, 0, 0.5);
    }

    .panel {
      background: var(--jha-component-background-color, #ffffff);
      border-radius: 8px;
      width: 600px;
      max-width: 100%;
      box-shadow:
        0 11px 15px -7px rgba(0, 0, 0, 0.2),
        0 24px 38px 3px rgba(0, 0, 0, 0.14),
        0 9px 46px 8px rgba(0, 0, 0, 0.12);
      overflow: hidden;
      position: relative;
    }

    .dialog-header {
      position: relative;
      padding: 24px 2px 15px;
    }

    .close-button {
      position: absolute;
      top: 8px;
      right: 8px;
      background: transparent;
      border: none;
      cursor: pointer;
      padding: 8px;
      color: var(--jha-text-light, #8c989f);
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
      transition: background-color 0.2s;
    }

    .close-button:hover {
      background: rgba(0, 0, 0, 0.04);
    }

    .title {
      font-size: var(--jha-dialog-header-font-size, 16px);
      font-weight: 500;
      color: var(--jha-text-dark, #212121);
      margin: 0 0 16px 0;
      line-height: 1.2;
      text-align: center;
    }

    .icon-container {
      display: flex;
      justify-content: center;
      margin: 0;
    }

    jha-icon-circle-warning {
      width: 120px;
      height: 120px;
      fill: var(--jha-color-warning, #fca902);
    }

    .icon-container.danger jha-icon-circle-warning {
      fill: var(--jha-color-danger, #d32f2f);
    }

    .icon-container.info jha-icon-circle-warning {
      fill: var(--jha-color-info, #2196f3);
    }

    jha-icon-circle-checkmark {
      width: 120px;
      height: 120px;
      fill: var(--jha-color-success, #2e7d32);
    }

    .dialog-content {
      padding: 3px 2px 24px;
    }

    .message {
      text-align: center;
      max-width: 400px;
      max-height: 300px;
      overflow-y: auto;
      margin: 0 auto 33px;
      font-size: 14px;
      line-height: 1.4em;
      color: var(--jha-text-light, #8c989f);
      padding: 0 8px;
      white-space: pre-wrap;
    }

    .buttons {
      display: flex;
      gap: 12px;
      justify-content: center;
      margin-top: 24px;
      flex-wrap: wrap;
      padding: 0 16px;
    }

    @media (prefers-color-scheme: dark) {
      .panel {
        background: var(--jha-component-background-color, #1e1e1e);
      }

      .title {
        color: var(--jha-text-dark, #ffffff);
      }

      .message {
        color: var(--jha-text-light, #b0b0b0);
      }

      .close-button {
        color: var(--jha-text-light, #b0b0b0);
      }

      .close-button:hover {
        background: rgba(255, 255, 255, 0.08);
      }
    }
  `;

  @property({ type: String })
  variant: JhftDialogVariant = "warning";

  @property({ type: String })
  dialogTitle = "Confirm";

  @property({ type: String })
  message = "Are you sure?";

  @property({ type: String })
  confirmLabel = "Confirm";

  @property({ type: String })
  cancelLabel = "Cancel";

  @property({ type: String })
  successLabel = "Done";

  @property({ type: String })
  retryLabel = "Try Again";

  @property({ type: String })
  closeLabel = "Close";

  /** When true, backdrop click does not dismiss. Default true. */
  @property({ type: Boolean })
  modal = true;

  @property({ type: Boolean })
  closeOnEsc = true;

  @property({ type: Boolean })
  showCloseButton = true;

  @property({ type: Boolean, reflect: true })
  open = false;

  @query("dialog")
  private dialogEl!: HTMLDialogElement;

  private _resolve: ((result: JhftDialogResult) => void) | null = null;
  private _opening = false;

  connectedCallback() {
    super.connectedCallback();
    // firstUpdated does not re-run on re-attach; bind here so ESC survives reconnect.
    this._bindDialogEl();
  }

  disconnectedCallback() {
    this._unbindEscFallback();
    this.dialogEl?.removeEventListener("cancel", this._onNativeCancel);
    this.dialogEl?.removeEventListener("click", this._onDialogClick);
    // hide() resolves a pending show() so teardown cannot hang the waiter
    this.hide();
    super.disconnectedCallback();
  }

  protected firstUpdated() {
    this._bindDialogEl();
    if (this.open) {
      void this._ensureOpen();
    }
  }

  private _bindDialogEl() {
    this.dialogEl?.removeEventListener("cancel", this._onNativeCancel);
    this.dialogEl?.removeEventListener("click", this._onDialogClick);
    this.dialogEl?.addEventListener("cancel", this._onNativeCancel);
    this.dialogEl?.addEventListener("click", this._onDialogClick);
  }

  protected updated(changed: Map<string, unknown>) {
    if (changed.has("open") && this.dialogEl && !this._opening) {
      if (this.open && !this.dialogEl.open && !this._isPopoverOpen(this.dialogEl)) {
        void this._ensureOpen();
      } else if (!this.open) {
        this._hideOverlay();
        this._resolvePending("cancel");
      }
    }
  }

  /**
   * Open the dialog. Returns a Promise that resolves with the user choice.
   * Safe to call repeatedly; previous waiter (if any) gets `cancel`.
   */
  show(): Promise<JhftDialogResult> {
    if (this._resolve) {
      this._resolve("cancel");
      this._resolve = null;
    }

    return new Promise((resolve) => {
      this._resolve = resolve;
      this.open = true;
      void this._ensureOpen();
    });
  }

  hide() {
    this.open = false;
    this._hideOverlay();
    this._resolvePending("cancel");
  }

  private _resolvePending(result: JhftDialogResult) {
    const resolve = this._resolve;
    this._resolve = null;
    resolve?.(result);
  }

  private _hideOverlay() {
    this._unbindEscFallback();
    const d = this.dialogEl;
    if (!d) return;
    if (this._usedPopover) {
      try {
        d.hidePopover();
      } catch {
        /* already closed */
      }
      d.removeAttribute("popover");
      this._usedPopover = false;
    }
    if (d.open) {
      d.close();
    }
  }

  /**
   * Always prefer showModal() (top layer: ESC `cancel`, ::backdrop, focus trap).
   * Nested non-modal show() inside DialogUtil's wizard is what dropped those.
   * If showModal throws (some engines refuse a second modal), popover is also
   * top-layer. Never show() while another light-DOM dialog is already open —
   * that overlay is inert and closeOnEsc never fires.
   */
  private async _ensureOpen() {
    if (this._opening) return;
    this._opening = true;
    try {
      await this.updateComplete;
      // hide() / disconnect during the await must not reopen
      if (!this.open) return;
      const d = this.dialogEl;
      if (!d || d.open || this._isPopoverOpen(d)) return;
      try {
        d.showModal();
        this._usedPopover = false;
        this._focusSafeAction();
      } catch {
        if (this._openAsPopover(d)) return;
        if (this._otherLightDialogOpen()) {
          // Cannot display over another top-layer dialog. Never auto-confirm —
          // a prompt the user never saw is not a decision.
          this._finish("cancel");
          return;
        }
        try {
          d.show();
          this._bindEscFallback();
          this._focusSafeAction();
        } catch {
          this._finish("cancel");
        }
      }
    } finally {
      this._opening = false;
    }
  }

  private _usedPopover = false;

  private _isPopoverOpen(d: HTMLDialogElement): boolean {
    return typeof d.matches === "function" && d.matches(":popover-open");
  }

  private _openAsPopover(d: HTMLDialogElement): boolean {
    const pop = d as HTMLElement & { showPopover?: () => void };
    if (typeof pop.showPopover !== "function") return false;
    d.setAttribute("popover", "manual");
    try {
      pop.showPopover();
      this._usedPopover = true;
      this._bindEscFallback();
      this._focusSafeAction();
      return true;
    } catch {
      d.removeAttribute("popover");
      return false;
    }
  }

  private _otherLightDialogOpen(): boolean {
    return Array.from(document.querySelectorAll("dialog")).some(
      (el) => (el as HTMLDialogElement).open,
    );
  }

  /** Non-modal / popover fallback has no native dialog `cancel` on ESC. */
  private _escFallback = (e: KeyboardEvent) => {
    if (e.key !== "Escape" && e.key !== "Esc") return;
    if (!this.closeOnEsc) return;
    e.preventDefault();
    e.stopPropagation();
    this._finish(this._dismissResult());
  };

  private _bindEscFallback() {
    document.addEventListener("keydown", this._escFallback, true);
  }

  private _unbindEscFallback() {
    document.removeEventListener("keydown", this._escFallback, true);
  }

  /**
   * Native `cancel` is how `<dialog showModal()>` reports ESC — it does not depend on
   * keydown focus inside the panel. Always preventDefault so the browser does not close
   * past us; then honor closeOnEsc. Backdrop is _onDialogClick only (cancel is not backdrop).
   * Arrow field so removeEventListener keeps the same identity across reconnect.
   */
  private _onNativeCancel = (e: Event) => {
    e.preventDefault();
    if (!this.closeOnEsc) return;
    this._finish(this._dismissResult());
  };

  /**
   * Click on the full-viewport dialog (outside `.panel`) = backdrop click.
   * modal=true  → ignore (Windows-like).
   * modal=false → dismiss as cancel (same as Stay / Cancel button).
   */
  private _onDialogClick = (e: MouseEvent) => {
    if (e.target !== this.dialogEl) return;
    if (this.modal) return;
    this._finish(this._dismissResult());
  };

  /**
   * Result for X, ESC, and non-modal backdrop — same mapping for all three.
   * success/error/notice → confirm (Done / Close / Log Out); otherwise → cancel (Stay).
   */
  private _dismissResult(): JhftDialogResult {
    if (this.variant === "success" || this.variant === "error" || this.variant === "notice") {
      return "confirm";
    }
    return "cancel";
  }

  /**
   * showModal() focuses the first focusable descendant. Keep Stay / Cancel /
   * Done / Retry first so Enter does not fire the X (Stay / dismiss).
   */
  private _focusSafeAction() {
    const host = this.renderRoot?.querySelector(".buttons jha-button") as HTMLElement | undefined;
    if (!host) return;
    const inner = host.shadowRoot?.querySelector("button") as HTMLElement | undefined;
    (inner ?? host).focus?.();
  }

  private _finish(result: JhftDialogResult) {
    if (!this._resolve && !this.open) return;
    const resolve = this._resolve;
    this._resolve = null;
    this.hide();
    this.dispatchEvent(
      new CustomEvent("jhft-dialog-result", {
        detail: { result },
        bubbles: true,
        composed: true,
      }),
    );
    resolve?.(result);
  }

  private onConfirm = () => this._finish("confirm");
  private onCancel = () => this._finish("cancel");
  private onRetry = () => this._finish("retry");

  private onCloseX = () => this._finish(this._dismissResult());

  private getIconClass(): string {
    if (this.variant === "success") return "success";
    if (this.variant === "error" || this.variant === "danger") return "danger";
    if (this.variant === "info") return "info";
    return "warning";
  }

  private renderIcon() {
    if (this.variant === "success") {
      return html`<jha-icon-circle-checkmark></jha-icon-circle-checkmark>`;
    }
    return html`<jha-icon-circle-warning></jha-icon-circle-warning>`;
  }

  private renderButtons() {
    if (this.variant === "success") {
      return html`
        <jha-button sync autofocus @click=${this.onConfirm}>${this.successLabel}</jha-button>
      `;
    }

    if (this.variant === "notice") {
      return html`
        <jha-button sync autofocus @click=${this.onConfirm}>${this.confirmLabel}</jha-button>
      `;
    }

    if (this.variant === "error") {
      return html`
        <jha-button outline sync autofocus @click=${this.onRetry}>${this.retryLabel}</jha-button>
        <jha-button sync @click=${this.onConfirm}>${this.closeLabel}</jha-button>
      `;
    }

    const confirmDanger = this.variant === "warning" || this.variant === "danger";
    return html`
      <jha-button outline sync autofocus @click=${this.onCancel}>${this.cancelLabel}</jha-button>
      <jha-button ?danger=${confirmDanger} sync @click=${this.onConfirm}>${this.confirmLabel}</jha-button>
    `;
  }

  render() {
    return html`
      <dialog
        aria-labelledby="jhft-dialog-title"
        aria-describedby="jhft-dialog-message"
      >
        <div class="panel">
          <div class="dialog-header">
            <h3 id="jhft-dialog-title" class="title">${this.dialogTitle}</h3>
            <div class="icon-container ${this.getIconClass()}">${this.renderIcon()}</div>
          </div>
          <div class="dialog-content">
            <p id="jhft-dialog-message" class="message">${this.message}</p>
            <div class="buttons">${this.renderButtons()}</div>
          </div>
          ${this.showCloseButton
        ? html`
                  <button class="close-button" type="button" aria-label="Close" @click=${this.onCloseX}>
                    <jha-icon-close size="24"></jha-icon-close>
                  </button>
                `
        : nothing}
        </div>
      </dialog>
    `;
  }
}

/**
 * Open a JHFT dialog and wait for the user's choice.
 * Always appends to `document.body` as a sibling of any DialogUtil modal so
 * showModal() can put the prompt on the top layer (ESC, backdrop, focus trap).
 * Nesting inside an already-open <dialog> forces non-modal show() and drops those.
 *
 * Only one helper-owned dialog at a time. A second `openJhftDialog` dismisses the
 * first as `cancel` (via disconnect) instead of stacking two body-level modals.
 * Declarative `<jhft-jha-dialog>` instances are left alone.
 */
let _helperDialog: JhftJhaDialog | null = null;

export function openJhftDialog(options: JhftDialogOptions = {}): Promise<JhftDialogResult> {
  const variant = options.variant ?? "warning";
  // Forced-logout notice reserved _openNotice first; a concurrent unsaved prompt must not
  // remove() the notice (that resolves logout waiters and redirects before Log Out).
  if (_openNotice && variant !== "notice") {
    return Promise.resolve("cancel");
  }

  if (_helperDialog) {
    _helperDialog.remove();
    _helperDialog = null;
  }

  const el = document.createElement("jhft-jha-dialog") as JhftJhaDialog;
  el.variant = variant;
  el.dialogTitle = options.title ?? "Confirm";
  el.message = options.message ?? "Are you sure?";
  el.confirmLabel = options.confirmLabel ?? defaultConfirmLabel(el.variant);
  el.cancelLabel = options.cancelLabel ?? defaultCancelLabel(el.variant);
  el.successLabel = options.successLabel ?? "Done";
  el.retryLabel = options.retryLabel ?? "Try Again";
  el.closeLabel = options.closeLabel ?? "Close";
  el.modal = options.modal !== false;
  el.closeOnEsc = options.closeOnEsc !== false;
  el.showCloseButton = options.showCloseButton !== false;

  _helperDialog = el;
  document.body.appendChild(el);

  return el.show().finally(() => {
    if (_helperDialog === el) {
      _helperDialog = null;
    }
    el.remove();
  });
}

function defaultConfirmLabel(variant: JhftDialogVariant): string {
  if (variant === "warning") return "Leave Without Saving";
  if (variant === "danger") return "Delete";
  if (variant === "notice") return "OK";
  return "Confirm";
}

/** Copy for every forced-logout notice, shared by the app and the dev lab. */
export const FORCED_LOGOUT_NOTICES = {
  sessionTimedOut: {
    title: "Session Timed Out",
    message: "Your session could not be renewed. Please log in again to continue.",
  },
  sessionExpired: {
    title: "Session Expired",
    message: "Your session has timed out. Please log in again to continue.",
  },
  accountLocked: {
    title: "Account Locked",
    message: "Maximum login attempts reached. Your account has been locked.",
  },
} as const;

export type ForcedLogoutNotice = keyof typeof FORCED_LOGOUT_NOTICES;

let _openNotice: Promise<JhftDialogResult> | null = null;

/**
 * One-button forced-logout notice. X and Esc resolve `confirm` too, so the caller
 * always goes on to log out; there is nothing for the user to decide.
 *
 * The shell timers and expired-token requests can both fire at once. Later callers
 * share the open notice. Reserve `_openNotice` before opening so unsaved prompts cannot
 * dismiss the notice in the same tick.
 */
export function openForcedLogoutNotice(kind: ForcedLogoutNotice): Promise<JhftDialogResult> {
  if (_openNotice) return _openNotice;
  const { title, message } = FORCED_LOGOUT_NOTICES[kind];
  let complete!: (result: JhftDialogResult) => void;
  _openNotice = new Promise<JhftDialogResult>((resolve) => {
    complete = resolve;
  });
  openJhftDialog({ variant: "notice", title, message, confirmLabel: "Log Out" })
    .then(complete)
    .finally(() => {
      _openNotice = null;
    });
  return _openNotice;
}

function defaultCancelLabel(variant: JhftDialogVariant): string {
  if (variant === "warning") return "Stay";
  return "Cancel";
}

declare global {
  interface HTMLElementTagNameMap {
    "jhft-jha-dialog": JhftJhaDialog;
  }
}
